import React, { useEffect, useState } from "react";
import { trackEvent } from "@/mock";
import { analyticsApi, assessmentApi, crmApi } from "@/services/phase1Api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import {
  Calendar,
  Check,
  ChevronRight,
  ChevronDown,
  FileText,
  Mail,
  RefreshCw,
  TrendingUp,
  User,
  ArrowRight,
  XCircle,
  Clock,
  AlertTriangle,
  Sparkles,
  BarChart3,
  Play,
  ExternalLink,
  UserCheck,
  Loader2,
} from "lucide-react";
import { MetricCard, StagePill, normalizeStage, stageOrder, PIPELINE_STAGES, stageAtLeast } from "./appShared";
import { assessmentApi as newAssessmentApi } from "../../services/assessmentApi";
import { useNavigate } from "react-router-dom";
import { AdminSalesDashboard } from "./AdminDashboard";
import { ConsultantWorkbench } from "./ConsultantWorkbench";
import { TenantDashboard } from "./CustomerPortal";

const STAGE_BADGE_STYLE = {
  NEW_INTAKE:               { bg: "#f3f4f6", fg: "#6b7280", label: "Intake" },
  DISQUALIFIED:             { bg: "#fef2f2", fg: "#b91c1c", label: "Disqualified" },
  NURTURE:                  { bg: "#f5f3ff", fg: "#7c3aed", label: "Nurture" },
  QUALIFIED:                { bg: "#eff6ff", fg: "#1a56db", label: "Qualified" },
  QUAL_CALL_BOOKED:         { bg: "#eff6ff", fg: "#1a56db", label: "Call Booked" },
  NO_SHOW:                  { bg: "#fffbeb", fg: "#b45309", label: "No Show" },
  QUALIFIED_TO_BUY:         { bg: "#eff6ff", fg: "#1a56db", label: "Ready to Buy" },
  FREE_ASSESSMENT_COMPLETE: { bg: "#ecfeff", fg: "#0891b2", label: "Free Assessment" },
  ASSESSMENT_PURCHASED:     { bg: "#fffbeb", fg: "#b45309", label: "Assessment" },
  ASSESSMENT_IN_PROGRESS:   { bg: "#fffbeb", fg: "#b45309", label: "Assessment" },
  ASSESSMENT_DELIVERED:     { bg: "#fffbeb", fg: "#b45309", label: "Assessment" },
  SCOPE_CONFIRMED:          { bg: "#f5f3ff", fg: "#7c3aed", label: "Scope" },
  SOW_SENT:                 { bg: "#f5f3ff", fg: "#7c3aed", label: "Scope" },
  SOW_APPROVED:             { bg: "#f5f3ff", fg: "#7c3aed", label: "Scope" },
  PROJECT_KICKOFF:          { bg: "#ecfdf5", fg: "#059669", label: "Project" },
  PROJECT_ACTIVE:           { bg: "#ecfdf5", fg: "#059669", label: "Project" },
  PROJECT_IN_DELIVERY:      { bg: "#ecfdf5", fg: "#059669", label: "Project" },
  PROJECT_COMPLETED:        { bg: "#ecfdf5", fg: "#059669", label: "Project" },
  PROJECT_DELIVERED:        { bg: "#ecfdf5", fg: "#059669", label: "Project" },
  RETAINER_OFFERED:         { bg: "#dbeafe", fg: "#1e40af", label: "Retainer" },
  RETAINER_ACTIVE:          { bg: "#dbeafe", fg: "#1e40af", label: "Retainer" },
  AT_RISK:                  { bg: "#fef2f2", fg: "#b91c1c", label: "At Risk" },
  CHURNED:                  { bg: "#f3f4f6", fg: "#6b7280", label: "Churned" },
};

const STAGE_FILTERS_LEADS = [
  { key: "all", label: "All" },
  { key: "intake", label: "Intake", matches: (s) => ["NEW_INTAKE", "DISQUALIFIED", "NURTURE"].includes(s) },
  { key: "qualified", label: "Qualified", matches: (s) => ["QUALIFIED", "QUAL_CALL_BOOKED", "NO_SHOW", "QUALIFIED_TO_BUY", "FREE_ASSESSMENT_COMPLETE"].includes(s) },
];

const STAGE_FILTERS_CUSTOMERS = [
  { key: "all", label: "All" },
  { key: "assessment", label: "Assessment", matches: (s) => ["ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS", "ASSESSMENT_DELIVERED"].includes(s) },
  { key: "scope", label: "Scope", matches: (s) => ["SCOPE_CONFIRMED", "SOW_SENT", "SOW_APPROVED"].includes(s) },
  { key: "project", label: "Project", matches: (s) => ["PROJECT_KICKOFF", "PROJECT_ACTIVE", "PROJECT_IN_DELIVERY", "PROJECT_COMPLETED", "PROJECT_DELIVERED"].includes(s) },
  { key: "retainer", label: "Retainer", matches: (s) => ["RETAINER_OFFERED", "RETAINER_ACTIVE"].includes(s) },
  { key: "risk", label: "At Risk", matches: (s) => ["AT_RISK", "CHURNED"].includes(s) },
];

const StageBadge = ({ stage }) => {
  const n = normalizeStage(stage);
  const meta = STAGE_BADGE_STYLE[n] || { bg: "#f3f4f6", fg: "#6b7280", label: n };
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full tracking-[0.04em]"
      style={{ backgroundColor: meta.bg, color: meta.fg }}>
      {meta.label}
    </span>
  );
};

const PipelineTable = ({ prospects, selectedId, onSelect, loading, isAdmin = false, pricing, mode = "leads" }) => {
  const [stageFilter, setStageFilter] = useState("all");
  const STAGE_FILTERS = mode === "customers" ? STAGE_FILTERS_CUSTOMERS : STAGE_FILTERS_LEADS;

  const filtered = prospects.filter((p) => {
    if (stageFilter === "all") return true;
    const f = STAGE_FILTERS.find((s) => s.key === stageFilter);
    return f?.matches ? f.matches(normalizeStage(p.stage)) : true;
  });

  const countFor = (key) => {
    if (key === "all") return prospects.length;
    const f = STAGE_FILTERS.find((s) => s.key === key);
    return prospects.filter((p) => f?.matches(normalizeStage(p.stage))).length;
  };

  const assessmentPrice = pricing?.pricing?.assessment?.amount;
  const fmtPrice = assessmentPrice ? `$${Math.round(assessmentPrice).toLocaleString()}` : "$4,995";

  return (
    <div className="rounded-[14px] border border-[#e5e7eb] bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#e5e7eb]">
        <div>
          <h2 className="text-[16px] font-bold text-[#111827] tracking-[-0.01em]">
            {mode === "customers" ? "Customer List" : "Lead List"}
          </h2>
          <div className="text-[11px] text-[#9ca3af] mt-0.5">
            {prospects.length} {mode === "customers" ? "customer" : "lead"}{prospects.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Stage filter pills */}
      <div className="px-4 py-3 border-b border-[#e5e7eb] bg-[#f9fafb] flex flex-wrap gap-1.5">
        {STAGE_FILTERS.map((f) => {
          const count = countFor(f.key);
          const active = stageFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setStageFilter(f.key)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold transition-all border ${
                active
                  ? "bg-[#1a56db] border-[#1a56db] text-white"
                  : "bg-white border-[#e5e7eb] text-[#6b7280] hover:border-[#bfdbfe] hover:text-[#1a56db]"
              }`}
              data-testid={`pipeline-filter-${f.key}`}
            >
              {f.label}
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                active ? "bg-white/20 text-white" : "bg-[#f3f4f6] text-[#6b7280]"
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Rows */}
      <div className="divide-y divide-[#f3f4f6]">
        {filtered.map((p) => {
          const stage = normalizeStage(p.stage);
          const value = stageOrder[stage] >= stageOrder.ASSESSMENT_PURCHASED ? fmtPrice : "—";
          const isSelected = selectedId === p.id;
          return (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              className={`w-full flex items-center justify-between gap-3 px-5 py-4 text-left transition-all ${
                isSelected
                  ? "bg-[#eff6ff] border-l-[3px] border-l-[#1a56db]"
                  : "hover:bg-[#f9fafb] border-l-[3px] border-l-transparent"
              }`}
              data-testid={`pipeline-row-${p.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold text-[#111827] truncate">
                  {p.account?.name || "—"}
                </div>
                <div className="text-[11px] text-[#9ca3af] mt-0.5 truncate">
                  {(p.account?.industry || "—").toLowerCase()}
                  {p.contact?.full_name && (
                    <>
                      <span className="mx-1">·</span>
                      {p.contact.full_name}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <StageBadge stage={p.stage} />
                {isAdmin && (
                  <span className="text-[12px] font-semibold text-[#4b5563] min-w-[60px] text-right">
                    {value}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="px-5 py-10 text-center text-[13px] text-[#9ca3af]">Loading pipeline…</div>
      ) : !filtered.length ? (
        <div className="px-5 py-10 text-center text-[13px] text-[#9ca3af]">
          {prospects.length === 0 ? "No opportunities yet. New intakes will appear here." : "No matches for this stage filter."}
        </div>
      ) : null}
    </div>
  );
};

export const ProspectAssessment = ({ session, prospect, onAssessmentStatusChange }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [activeResult, setActiveResult] = useState(null);

  const INDUSTRY_MAP = { healthcare: "medical", education: "charter_school", legal: "professional_services", finance: "accounting_finance" };
  const rawIndustry = prospect.account?.industry || "general";
  const industry = INDUSTRY_MAP[rawIndustry.toLowerCase()] || rawIndustry.toLowerCase().replace(/\s+/g, "_");
  const stage = normalizeStage(prospect.stage);
  const isPaidStage = stageAtLeast(stage, "ASSESSMENT_PURCHASED");
  const isDetailedPaid = !!prospect.payment?.detailed_assessment_paid;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [tmpls, sess] = await Promise.all([
          newAssessmentApi.listTemplates({ industry, status: "published" }, session),
          newAssessmentApi.listSessions({ prospect_id: prospect.id }, session),
        ]);
        let allTmpls = tmpls;
        if (!tmpls.length || !tmpls.some(t => t.industry === industry)) {
          const generalTmpls = await newAssessmentApi.listTemplates({ industry: "general", status: "published" }, session);
          allTmpls = [...tmpls, ...generalTmpls];
        }
        if (mounted) {
          setTemplates(allTmpls);
          setSessions(sess);
          // Notify parent about assessment completion status
          const completedSess = sess.filter(s => s.status === "completed");
          const hasCompletedInitial = completedSess.some(s => s.assessment_type === "initial");
          const hasCompletedDetailed = completedSess.some(s => s.assessment_type === "detailed");
          if (onAssessmentStatusChange) {
            onAssessmentStatusChange({ hasCompletedInitial, hasCompletedDetailed, completedSessions: completedSess });
          }
        }
      } catch {
        // silent
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [prospect.id, industry, session, onAssessmentStatusChange]);

  if (activeSession) {
    return (
      <Card className="rounded-3xl border-border bg-foreground/5 p-5 backdrop-blur-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="text-base font-semibold text-foreground">Taking Assessment</div>
          <Button size="sm" onClick={() => setActiveSession(null)}
            className="h-8 rounded-lg text-sm border border-border bg-foreground/5 text-foreground">
            Back to Overview
          </Button>
        </div>
        <div className="text-sm text-foreground/50">
          Assessment session in progress. Navigate to{" "}
          <button onClick={() => navigate(`/app/assessment-templates`)} className="text-[var(--brand)] underline">
            Assessments
          </button>{" "}
          to complete the full assessment experience with progress tracking.
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="rounded-3xl border-border bg-foreground/5 p-5 backdrop-blur-2xl">
        <div className="text-base text-foreground/50">Loading assessment data...</div>
      </Card>
    );
  }

  // ── FLOW ENFORCEMENT ──
  // Pre-payment: Show ONLY initial (free) templates
  // Post-payment (detailed_assessment_paid): Show ONLY detailed templates
  const initialTemplates = templates.filter(t => t.type === "initial");
  const detailedTemplates = templates.filter(t => t.type === "detailed");
  const visibleTemplates = isDetailedPaid ? detailedTemplates : initialTemplates;
  const completedSessions = sessions.filter(s => s.status === "completed");
  const inProgressSessions = sessions.filter(s => s.status === "in_progress" || s.status === "not_started");

  const handleStartAssessment = async (templateId) => {
    setStarting(true);
    try {
      const sess = await newAssessmentApi.createSession({ template_id: templateId, prospect_id: prospect.id }, session);
      navigate(`/app/assessment-templates?take=${sess.id}`);
    } catch {
      toast.error("Failed to start assessment");
    } finally {
      setStarting(false);
    }
  };

  return (
    <Card className="rounded-3xl border-border bg-foreground/5 p-5 backdrop-blur-2xl" data-testid="prospect-assessment">
      <div className="text-base font-semibold text-foreground mb-4">
        {isDetailedPaid ? "Detailed AI Readiness Assessment" : "Initial AI Readiness Assessment (Free)"}
      </div>

      {/* Current phase indicator */}
      <div className="mb-4 p-3 rounded-xl border border-border bg-muted/60">
        <div className="text-xs text-foreground/40 uppercase tracking-wider mb-1">Current Phase</div>
        <div className="text-sm text-foreground">
          {isDetailedPaid
            ? "Paid Assessment — Complete the in-depth assessment, then generate the Action Plan."
            : "Free Assessment — Complete the preliminary assessment, then advance to paid assessment stage."}
        </div>
      </div>

      {/* Existing Sessions */}
      {sessions.length > 0 && (
        <div className="mb-4 space-y-2">
          <div className="text-xs text-foreground/50 uppercase">Assessment Sessions</div>
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted">
              <div>
                <div className="text-sm text-foreground font-medium">{s.assessment_type === "initial" ? "Preliminary Assessment" : "In-Depth Assessment"}</div>
                <div className="text-xs text-foreground/40">
                  {s.status === "completed" ? "Completed" : `${s.progress_percent || 0}% complete`}
                  {" | "}
                  {s.intake_industry || industry}
                </div>
              </div>
              <div className="flex gap-2">
                {s.status === "completed" && (
                  <Button size="sm" onClick={async () => {
                    try {
                      const full = await newAssessmentApi.getSession(s.id, session);
                      if (full.result) setActiveResult(full.result);
                    } catch { toast.error("Failed to load results"); }
                  }} className="h-8 rounded-lg text-sm bg-emerald-600 text-foreground" data-testid="view-results-btn">
                    <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Results
                  </Button>
                )}
                {(s.status === "in_progress" || s.status === "not_started") && (
                  <Button size="sm" onClick={() => navigate(`/app/assessment-templates?take=${s.id}`)}
                    className="h-8 rounded-lg text-sm bg-[var(--brand)] text-white" data-testid="resume-assessment-btn">
                    <Play className="h-3.5 w-3.5 mr-1.5" /> Continue
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results View */}
      {activeResult && (
        <div className="mb-4 p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-emerald-300">Assessment Results</div>
            <Button size="sm" onClick={() => setActiveResult(null)} className="h-7 rounded-lg text-xs border border-border bg-foreground/5 text-foreground">Close</Button>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center p-2 rounded-lg border border-border bg-muted/60">
              <div className="text-2xl font-bold text-foreground">{activeResult.overall_readiness_score}</div>
              <div className="text-xs text-foreground/40">Readiness Score</div>
            </div>
            <div className="text-center p-2 rounded-lg border border-border bg-muted/60">
              <div className="text-lg font-bold text-foreground">{activeResult.readiness_band}</div>
              <div className="text-xs text-foreground/40">Maturity Band</div>
            </div>
            <div className="text-center p-2 rounded-lg border border-border bg-muted/60">
              <div className="text-2xl font-bold text-foreground">{activeResult.gating_flags?.length || 0}</div>
              <div className="text-xs text-foreground/40">Gating Flags</div>
            </div>
          </div>
          {activeResult.readiness_scores_by_pillar?.map((ps) => (
            <div key={ps.pillar} className="flex items-center gap-2 py-1">
              <span className="text-sm text-foreground/50 w-48 truncate">{ps.label}</span>
              <div className="flex-1 h-2 bg-foreground/10 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${ps.score >= 3.0 ? "bg-emerald-500" : ps.score >= 2.0 ? "bg-yellow-500" : "bg-red-500"}`}
                  style={{ width: `${(ps.score / 4) * 100}%` }} />
              </div>
              <span className="text-sm text-foreground w-8">{ps.score}</span>
            </div>
          ))}
          {activeResult.id && !activeResult.implementation_plan && (
            <Button size="sm" onClick={() => navigate(`/app/assessment-templates`)}
              className="mt-3 h-8 rounded-lg text-sm bg-[var(--brand)] text-white">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate Implementation Plan
            </Button>
          )}
        </div>
      )}

      {/* Available Templates - FLOW GATED */}
      {visibleTemplates.length > 0 && !inProgressSessions.length && !completedSessions.some(s =>
        isDetailedPaid ? s.assessment_type === "detailed" : s.assessment_type === "initial"
      ) ? (
        <div className="space-y-3">
          <div className="text-xs text-foreground/50 uppercase">
            {isDetailedPaid ? "In-Depth Assessment" : "Free Preliminary Assessment"} for {industry.replace(/_/g, " ")}
          </div>
          {visibleTemplates.map((t) => (
            <div key={t.id} className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted">
              <div>
                <div className="text-sm text-foreground font-medium">{t.title}</div>
                <div className="text-xs text-foreground/40">{t.question_count} questions | {t.type === "initial" ? "Free" : "Paid"}</div>
              </div>
              <Button size="sm" disabled={starting} onClick={() => handleStartAssessment(t.id)}
                className="h-9 rounded-lg text-sm bg-[var(--brand)] text-white font-medium" data-testid={`start-assessment-${t.id}`}>
                <Play className="h-3.5 w-3.5 mr-1.5" /> Start Assessment
              </Button>
            </div>
          ))}
        </div>
      ) : visibleTemplates.length === 0 && (
        <div className="text-center py-6 text-sm text-foreground/50">
          No {isDetailedPaid ? "detailed" : "initial"} assessment templates found for <span className="text-foreground capitalize">{industry.replace(/_/g, " ")}</span>.
        </div>
      )}
    </Card>
  );
};

const AssessmentEditor = ({
  session,
  prospect,
  templateId,
  gated,
  gateLabel,
}) => {
  const [template, setTemplate] = useState(null);
  const [rs, setRs] = useState(null);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);

  const completion = rs?.score?.completion ?? 0;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const q = await assessmentApi.getQuestionnaire(templateId, session);
        const state = await assessmentApi.getResponseSet(
          { prospect_id: prospect.id, template_id: templateId },
          session,
        );
        if (!mounted) return;
        setTemplate(q);
        setRs({
          ...state.response_set,
          responses: state.responses,
          score: state.score,
        });
      } catch (e) {
        toast.error("Could not load assessment", {
          description: e?.response?.data?.detail || e?.message || "Try again.",
        });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [prospect.id, templateId, session]);

  return (
    <Card className="rounded-3xl border-border bg-foreground/5 p-5 backdrop-blur-2xl">
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div>
          <div className="text-sm font-semibold text-foreground">
            {template?.title || "Loading…"}
          </div>
          <div className="mt-1 text-xs text-foreground/55">
            Completion: <span className="text-foreground">{completion}%</span>
            {rs?.status === "locked" ? (
              <span className="ml-2 text-foreground/45">· locked</span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          {gated ? (
            <Button
              onClick={() => toast.message("Gated", { description: gateLabel })}
              className="h-10 rounded-none border border-border bg-foreground/5 text-foreground hover:bg-foreground hover:text-background"
              variant="secondary"
            >
              Unlock required
            </Button>
          ) : (
            <Button
              disabled={rs?.status === "locked" || busy}
              onClick={async () => {
                if (!rs) {
                  toast.message("Nothing to lock", {
                    description: "Answer at least one question first.",
                  });
                  return;
                }
                setBusy(true);
                try {
                  const locked = await assessmentApi.lockResponseSet(
                    { response_set_id: rs.id },
                    session,
                  );
                  setRs((prev) => ({ ...(prev || {}), ...locked }));
                  analyticsApi.track("assessment_complete", { response_set_id: rs.id }, session);
                  toast.success("Locked", {
                    description:
                      "Assessment locked. Only Super Admin can reopen.",
                  });
                } catch (e) {
                  toast.error("Could not lock", {
                    description: e?.message || "Try again.",
                  });
                } finally {
                  setBusy(false);
                }
              }}
              className="h-10 rounded-none bg-[var(--brand)] text-white hover:bg-[var(--brand)]"
            >
              Lock responses
            </Button>
          )}
        </div>
      </div>

      <Separator className="my-4 bg-foreground/10" />

      <div className="space-y-4">
        {(template?.questions || []).slice(0, 12).map((q) => {
          const current = rs?.responses?.[q.id]?.value || "";
          const currentNotes = notes[q.id] ?? rs?.responses?.[q.id]?.notes ?? "";

          return (
            <div
              key={q.id}
              className="rounded-2xl border border-border bg-muted p-4"
            >
              <div className="text-sm text-foreground">{q.text}</div>
              <div className="mt-3 grid gap-3 md:grid-cols-[220px_1fr]">
                <Select
                  disabled={gated || rs?.status === "locked"}
                  value={current}
                  onValueChange={async (val) => {
                    setBusy(true);
                    try {
                      const out = await assessmentApi.upsertResponse(
                        {
                          prospect_id: prospect.id,
                          template_id: templateId,
                          question_id: q.id,
                          value: val,
                          notes: currentNotes,
                        },
                        session,
                      );
                      setRs((prev) => {
                        const next = {
                          ...(prev || {}),
                          ...(out?.response_set || {}),
                          responses: {
                            ...(prev?.responses || {}),
                            [q.id]: out?.response,
                          },
                          score: out?.score,
                        };
                        return next;
                      });
                      toast.message("Saved", {
                        description: `${q.id} updated.`,
                      });
                    } catch (e) {
                      toast.error("Could not save", {
                        description: e?.message || "Try again.",
                      });
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <SelectTrigger className="h-10 rounded-none border-border bg-muted text-foreground">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="border-border bg-background text-foreground">
                    {q.options.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Textarea
                  disabled={gated || rs?.status === "locked"}
                  value={currentNotes}
                  onChange={(e) =>
                    setNotes((prev) => ({ ...prev, [q.id]: e.target.value }))
                  }
                  placeholder="Consultant notes (optional)"
                  className="min-h-[40px] rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                  onBlur={async () => {
                    if (!current) return;
                    try {
                      const out = await assessmentApi.upsertResponse(
                        {
                          prospect_id: prospect.id,
                          template_id: templateId,
                          question_id: q.id,
                          value: current,
                          notes: currentNotes,
                        },
                        session,
                      );
                      setRs((prev) => ({
                        ...(prev || {}),
                        ...(out?.response_set || {}),
                        responses: {
                          ...(prev?.responses || {}),
                          [q.id]: out?.response,
                        },
                        score: out?.score,
                      }));
                    } catch {
                      // ignore
                    }
                  }}
                />
              </div>

              <div className="mt-2 text-xs text-foreground/45">
                Category: {q.category}
              </div>
            </div>
          );
        })}

        <div className="rounded-2xl border border-border bg-muted p-4 text-xs text-foreground/50">
          The editor shows the first 12 questions. Full templates, weights, exports, and category scoring are available in the complete assessment.
        </div>
      </div>
    </Card>
  );
};

export const STAGE_TRANSITIONS = {
  NEW_INTAKE: {
    nextStep: "Review and qualify this prospect",
    actions: [
      { target: "QUALIFIED", label: "Qualify Prospect", variant: "primary" },
      { target: "DISQUALIFIED", label: "Disqualify", variant: "destructive" },
      { target: "NURTURE", label: "Move to Nurture", variant: "secondary" },
    ],
  },
  QUALIFIED: {
    nextStep: "Qualify and begin the free assessment",
    actions: [
      { target: "QUALIFIED_TO_BUY", label: "Begin Free Assessment", variant: "primary" },
      { target: "DISQUALIFIED", label: "Disqualify", variant: "destructive" },
      { target: "NURTURE", label: "Move to Nurture", variant: "secondary" },
    ],
  },
  QUAL_CALL_BOOKED: {
    nextStep: "Complete the qualification call, then mark the outcome",
    actions: [
      { target: "QUALIFIED_TO_BUY", label: "Mark Qualified to Buy", variant: "primary" },
      { target: "NO_SHOW", label: "No Show", variant: "destructive" },
    ],
  },
  NO_SHOW: {
    nextStep: "Rebook the call or reassign",
    actions: [
      { target: "QUAL_CALL_BOOKED", label: "Rebook Call", variant: "primary" },
      { target: "DISQUALIFIED", label: "Disqualify", variant: "destructive" },
      { target: "NURTURE", label: "Move to Nurture", variant: "secondary" },
    ],
  },
  QUALIFIED_TO_BUY: {
    nextStep: "Complete the free Preliminary Assessment first, then advance",
    assessmentGated: "initial",
    actions: [
      { target: "FREE_ASSESSMENT_COMPLETE", label: "Mark Free Assessment Complete", variant: "primary", requiresAssessment: "initial" },
      { target: "NURTURE", label: "Move to Nurture", variant: "secondary" },
    ],
  },
  FREE_ASSESSMENT_COMPLETE: {
    nextStep: "Send the prospect access to their portal — they can review results and purchase the In-Depth Assessment from there",
    gate: 1,
    actions: [
      { target: "SEND_PORTAL_ACCESS", label: "Send Portal Access", variant: "primary", isPortalAccess: true },
      { target: "NURTURE", label: "Move to Nurture", variant: "secondary" },
    ],
  },
  ASSESSMENT_PURCHASED: {
    nextStep: "Begin the paid in-depth assessment",
    actions: [
      { target: "ASSESSMENT_IN_PROGRESS", label: "Begin Paid Assessment", variant: "primary" },
    ],
  },
  ASSESSMENT_IN_PROGRESS: {
    nextStep: "Complete the paid In-Depth Assessment, then deliver",
    assessmentGated: "detailed",
    actions: [
      { target: "ASSESSMENT_DELIVERED", label: "Mark Paid Assessment Delivered", variant: "primary", requiresAssessment: "detailed" },
    ],
  },
  ASSESSMENT_DELIVERED: {
    nextStep: "Awaiting scope confirmation from the customer. They must approve their Implementation Plan in the portal before the Scope of Services can be generated.",
    gate: 2,
    actions: [
      { target: "SCOPE_CONFIRMED", label: "Scope Confirmed (Auto)", variant: "secondary", requiresScopeConfirm: true },
    ],
  },
  SCOPE_CONFIRMED: {
    nextStep: "Scope is confirmed. Generate the Scope of Services for client approval.",
    gate: 2,
    actions: [
      { target: "SOW_GENERATED", label: "Proceed to Scope of Services & Project", variant: "primary", isProceedToSOW: true },
    ],
  },
  SOW_APPROVED: {
    nextStep: "GATE 2 — Collect payment for the Implementation Project",
    gate: 2,
    actions: [
      { target: "PROJECT_IN_DELIVERY", label: "Record Payment — Implementation Project ($9,999)", variant: "payment", productKey: "project" },
    ],
  },
  PROJECT_IN_DELIVERY: {
    nextStep: "Complete and deliver the implementation project",
    actions: [
      { target: "PROJECT_DELIVERED", label: "Mark Project Delivered", variant: "primary" },
      { target: "AT_RISK", label: "Flag At Risk", variant: "destructive" },
    ],
  },
  PROJECT_DELIVERED: {
    nextStep: "GATE 3 — Collect payment for the Monthly Retainer",
    gate: 3,
    actions: [
      { target: "RETAINER_ACTIVE", label: "Record Payment — Monthly Retainer ($2,500/mo)", variant: "payment", productKey: "retainer" },
    ],
  },
  RETAINER_OFFERED: {
    nextStep: "GATE 3 — Collect payment for the Monthly Retainer",
    gate: 3,
    actions: [
      { target: "RETAINER_ACTIVE", label: "Record Payment — Monthly Retainer ($2,500/mo)", variant: "payment", productKey: "retainer" },
      { target: "CHURNED", label: "Mark Churned", variant: "destructive" },
    ],
  },
  RETAINER_ACTIVE: {
    nextStep: "Client is on active retainer. Monitor and maintain.",
    actions: [
      { target: "AT_RISK", label: "Flag At Risk", variant: "destructive" },
      { target: "CHURNED", label: "Mark Churned", variant: "destructive" },
    ],
  },
  AT_RISK: {
    nextStep: "Resolve the risk or escalate",
    actions: [
      { target: "RETAINER_ACTIVE", label: "Resolve — Back to Active", variant: "primary" },
      { target: "CHURNED", label: "Mark Churned", variant: "destructive" },
    ],
  },
  DISQUALIFIED: {
    nextStep: "Prospect is disqualified. Reopen if circumstances change.",
    actions: [
      { target: "NEW_INTAKE", label: "Reopen", variant: "secondary" },
    ],
  },
  NURTURE: {
    nextStep: "Prospect is in nurture. Re-qualify when ready.",
    actions: [
      { target: "QUALIFIED", label: "Re-qualify", variant: "primary" },
      { target: "DISQUALIFIED", label: "Disqualify", variant: "destructive" },
    ],
  },
  CHURNED: {
    nextStep: "Client has churned. Reopen if they return.",
    actions: [
      { target: "NEW_INTAKE", label: "Reopen", variant: "secondary" },
    ],
  },
};

const StageActions = ({ session, prospect, stage, onRefresh, assessmentStatus, pricing }) => {
  const [busy, setBusy] = useState(false);
  const config = STAGE_TRANSITIONS[stage] || {};
  const transitions = config.actions || [];
  const isAdmin = session.user.role === "admin";
  const { hasCompletedInitial, hasCompletedDetailed } = assessmentStatus || {};
  const hasConsultant = !!prospect.assigned_consultant_id;

  // Build dynamic price labels from pricing data
  const fmtAmt = (amt) => `$${Math.round(amt).toLocaleString()}`;
  const assessmentAmt = pricing?.pricing?.assessment?.amount;
  const implementationAmt = pricing?.pricing?.implementation?.amount;
  const retainerPlans = pricing?.retainer_plans || [];
  const avgRetainer = retainerPlans.length > 0
    ? retainerPlans.reduce((s, p) => s + (p.monthly_price || 0), 0) / retainerPlans.length
    : null;

  // Replace hardcoded prices in labels dynamically
  const getDynamicLabel = (t) => {
    if (t.isPortalAccess && prospect.provisioned_tenant_id) {
      return "Resend Portal Access";
    }
    if (t.productKey === "detailed" && assessmentAmt) {
      return `Record Payment — Paid Assessment (${fmtAmt(assessmentAmt)})`;
    }
    if (t.productKey === "project" && implementationAmt) {
      return `Record Payment — Implementation Project (${fmtAmt(implementationAmt)})`;
    }
    if (t.productKey === "retainer") {
      if (retainerPlans.length === 1) {
        return `Record Payment — Monthly Retainer (${fmtAmt(retainerPlans[0].monthly_price)}/mo)`;
      }
      if (avgRetainer) {
        return `Record Payment — Monthly Retainer (from ${fmtAmt(Math.min(...retainerPlans.map(p => p.monthly_price)))}/mo)`;
      }
    }
    return t.label;
  };

  if (!transitions.length) return null;

  const isActionGated = (transition) => {
    if (transition.requiresAssessment === "initial" && !hasCompletedInitial) return true;
    if (transition.requiresAssessment === "detailed" && !hasCompletedDetailed) return true;
    if (transition.requiresScopeConfirm) return "scope";
    // Payment-recording actions should NOT be gated by consultant assignment —
    // payment can be recorded first, consultant assigned later.
    if (transition.variant === "payment") return false;
    // Gate assessment steps if no consultant assigned
    const consultantRequired = ["ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS", "ASSESSMENT_DELIVERED"].includes(transition.target);
    if (consultantRequired && !hasConsultant) return "consultant";
    return false;
  };

  const handleAdvance = async (transition) => {
    setBusy(true);
    try {
      if (transition.isPortalAccess) {
        const res = await fetch(`${BACKEND}/api/prospects/${prospect.id}/send-portal-access`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-id": session.tenant?.id || "tenant_1",
            "x-user-id": session.user?.id || "admin",
            "x-user-role": session.user?.role || "admin",
          },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Failed to send portal access");
        }
        const data = await res.json();
        if (data.invite_link) {
          await navigator.clipboard.writeText(data.invite_link).catch(() => {});
        }
        toast.success("Portal access sent", { description: `Link copied: ${data.invite_link}` });
      } else if (transition.isProceedToSOW) {
        // Call proceed-to-sow endpoint
        const res = await fetch(`${BACKEND}/api/prospects/${prospect.id}/proceed-to-sow`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-tenant-id": session.tenant?.id || "tenant_1",
            "x-user-id": session.user?.id || "admin",
            "x-user-role": session.user?.role || "admin",
          },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed to proceed to Scope of Services");
        toast.success("Tenant provisioned & Scope of Services generated!", { description: data.message?.substring(0, 80) });
      } else if (transition.productKey) {
        await crmApi.mockPayment(
          { prospect_id: prospect.id, product_key: transition.productKey },
          session,
        );
        trackEvent("payment_recorded", { prospect_id: prospect.id, product: transition.productKey });
        analyticsApi.track("payment_recorded", { prospect_id: prospect.id, product: transition.productKey }, session);
        toast.success("Payment recorded", { description: `Stage advanced to ${PIPELINE_STAGES[transition.target]?.label || transition.target}` });
      } else {
        await crmApi.updateProspect(
          { prospect_id: prospect.id, stage: transition.target },
          session,
        );
        trackEvent("stage_advanced", { prospect_id: prospect.id, to: transition.target });
        analyticsApi.track("stage_advanced", { prospect_id: prospect.id, to: transition.target }, session);
        toast.success("Stage updated", { description: `Moved to ${PIPELINE_STAGES[transition.target]?.label || transition.target}` });
      }
      onRefresh();
    } catch (e) {
      console.error("handleAdvance error:", e);
      toast.error("Action failed", { description: e?.message || "Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const getButtonClasses = (variant) => {
    switch (variant) {
      case "primary":
        return "bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90";
      case "payment":
        return "bg-emerald-600 text-foreground hover:bg-emerald-700 ring-1 ring-emerald-400/30";
      case "destructive":
        return "border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20";
      case "secondary":
      default:
        return "border border-border bg-foreground/5 text-foreground hover:bg-foreground/10";
    }
  };

  const getIcon = (variant, target) => {
    if (variant === "payment") return <Check className="mr-2 h-4 w-4" />;
    if (variant === "destructive") {
      if (target === "DISQUALIFIED" || target === "CHURNED") return <XCircle className="mr-2 h-4 w-4" />;
      if (target === "NO_SHOW") return <Clock className="mr-2 h-4 w-4" />;
      if (target === "AT_RISK") return <AlertTriangle className="mr-2 h-4 w-4" />;
      return null;
    }
    if (variant === "primary") return <ArrowRight className="mr-2 h-4 w-4" />;
    return null;
  };

  return (
    <div className="mt-4 rounded-xl border border-border bg-muted/60 p-4" data-testid="stage-actions">
      {/* Next Step Banner */}
      <div className={`flex items-start gap-3 mb-4 p-3 rounded-lg ${
        config.gate ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-foreground/5 border border-border"
      }`}>
        <div className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
          config.gate ? "bg-emerald-500 text-white" : "bg-[var(--brand)] text-white"
        }`}>
          {config.gate || "!"}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-foreground/40">Next Step</div>
          <div className={`text-sm font-medium ${config.gate ? "text-emerald-300" : "text-foreground"}`}>
            {config.nextStep}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        {transitions.map((t) => {
          const showPaymentOnly = t.productKey && !isAdmin;
          if (showPaymentOnly) return null;
          const gated = isActionGated(t);

          return (
            <div key={t.target} className="flex flex-col gap-1">
              <Button
                disabled={busy || !!gated}
                onClick={() => handleAdvance(t)}
                className={`h-10 rounded-none text-sm font-medium ${getButtonClasses(t.variant)} ${gated ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid={`stage-action-${t.target.toLowerCase()}`}
              >
                {getIcon(t.variant, t.target)}
                {getDynamicLabel(t)}
              </Button>
              {gated === "consultant" && (
                <span className="text-[10px] text-amber-400/70 flex items-center gap-1">
                  <UserCheck className="h-3 w-3" />
                  Assign a consultant first
                </span>
              )}
              {gated === "scope" && (
                <span className="text-[10px] text-sky-400/70 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Waiting for customer to confirm scope in their portal
                </span>
              )}
              {gated === true && (
                <span className="text-[10px] text-[#007a9e] dark:text-amber-400/70 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Complete the {t.requiresAssessment === "initial" ? "Initial" : "Detailed"} Assessment first
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EnrichWithAIButton = ({ session, prospectId, existingEnrichment, onRefresh }) => {
  const [enriching, setEnriching] = useState(false);
  return (
    <Button
      size="sm"
      disabled={enriching}
      onClick={async () => {
        setEnriching(true);
        try {
          await crmApi.enrichWithAI({ prospect_id: prospectId }, session);
          toast.success("AI enrichment complete");
          onRefresh();
        } catch (e) {
          const msg = e?.response?.data?.detail || e?.message || "Try again.";
          toast.error("Enrichment failed", { description: msg });
        } finally {
          setEnriching(false);
        }
      }}
      className="h-8 rounded-lg text-xs bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90 font-medium"
      data-testid="enrich-ai-btn"
    >
      {enriching ? (
        <><RefreshCw className="h-3 w-3 mr-1.5 animate-spin" /> Enriching...</>
      ) : (
        <><Sparkles className="h-3 w-3 mr-1.5" /> {existingEnrichment ? "Re-enrich with AI" : "Enrich with AI"}</>
      )}
    </Button>
  );
};

/* ─── Pipeline Stage Progress Tracker for Prospect Panel ─── */
const STAGE_PHASES = [
  { key: "intake", label: "Intake", stages: ["NEW_INTAKE"] },
  { key: "qualified", label: "Qualified", stages: ["QUALIFIED", "QUAL_CALL_BOOKED", "QUALIFIED_TO_BUY", "NO_SHOW"] },
  { key: "assessment", label: "Assessment", stages: ["ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS", "ASSESSMENT_DELIVERED"] },
  { key: "sow", label: "Scope of Services", stages: ["SOW_SENT", "SOW_APPROVED"] },
  { key: "project", label: "Project", stages: ["PROJECT_IN_DELIVERY", "PROJECT_DELIVERED"] },
  { key: "retainer", label: "Retainer", stages: ["RETAINER_ACTIVE"] },
];

const ProspectStageTracker = ({ stage }) => {
  const normalized = normalizeStage(stage);
  const currentOrder = stageOrder[normalized] || 0;

  const getPhaseStatus = (phase) => {
    const phaseMinOrder = Math.min(...phase.stages.map(s => stageOrder[s] || 999));
    const phaseMaxOrder = Math.max(...phase.stages.map(s => stageOrder[s] || 0));
    if (currentOrder > phaseMaxOrder) return "complete";
    if (currentOrder >= phaseMinOrder && currentOrder <= phaseMaxOrder) return "active";
    return "upcoming";
  };

  return (
    <div className="mt-4 px-1" data-testid="prospect-stage-tracker">
      <div className="flex items-center justify-between gap-0">
        {STAGE_PHASES.map((phase, idx) => {
          const status = getPhaseStatus(phase);
          const isLast = idx === STAGE_PHASES.length - 1;
          return (
            <React.Fragment key={phase.key}>
              <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold border-2 transition-all ${
                  status === "complete"
                    ? "bg-[#10b981] border-[#10b981] text-white"
                    : status === "active"
                      ? "bg-[#1a56db] border-[#1a56db] text-white"
                      : "bg-white border-[#d1d5db] text-[#9ca3af]"
                }`}>
                  {status === "complete" ? <Check className="h-4 w-4" strokeWidth={3} /> : idx + 1}
                </div>
                <span className={`text-[10px] font-semibold text-center leading-tight ${
                  status === "complete" ? "text-[#059669]" : status === "active" ? "text-[#1a56db]" : "text-[#9ca3af]"
                }`}>{phase.label}</span>
              </div>
              {!isLast && (
                <div className={`h-[2px] flex-1 max-w-[40px] -mt-5 rounded-full ${
                  getPhaseStatus(STAGE_PHASES[idx + 1]) === "complete" || status === "complete"
                    ? "bg-[#10b981]"
                    : status === "active" ? "bg-[#1a56db]" : "bg-[#e5e7eb]"
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const ProspectPanel = ({ session, prospectId, onRefresh, pricing }) => {
  const [newNote, setNewNote] = useState("");
  const [prospect, setProspect] = useState(null);
  const [assessmentStatus, setAssessmentStatus] = useState({});
  const isAdmin = session.user.role === "admin";
  const isConsultant = session.user.role === "consultant" || isAdmin;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await crmApi.getProspect({ prospect_id: prospectId }, session);
        if (mounted) setProspect(data);
      } catch (e) {
        toast.error("Could not load prospect", {
          description: e?.response?.data?.detail || e?.message || "Try again.",
        });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [prospectId, session, onRefresh]);

  if (!prospect) {
    return (
      <Card className="rounded-3xl border-border bg-foreground/5 p-6 text-foreground/60 backdrop-blur-2xl">
        Select a prospect to view details.
      </Card>
    );
  }

  const stage = normalizeStage(prospect.stage);
  const detailedGated = !prospect.payment?.detailed_assessment_paid;

  // Determine next best action
  const getNextAction = () => {
    switch (stage) {
      case "NEW_INTAKE": return { label: "Review intake and qualify", action: "qualify" };
      case "QUALIFIED": return { label: "Book qualification call", action: "book_call" };
      case "QUAL_CALL_BOOKED": return { label: "Prepare for call", action: "prep" };
      case "NO_SHOW": return { label: "Follow up on missed call", action: "followup" };
      case "QUALIFIED_TO_BUY": return { label: "Send assessment proposal", action: "propose" };
      case "ASSESSMENT_PURCHASED": return { label: "Begin assessment", action: "start_assessment" };
      case "ASSESSMENT_IN_PROGRESS": return { label: "Complete assessment delivery", action: "deliver" };
      case "ASSESSMENT_DELIVERED": return { label: "Present findings and propose project", action: "propose_project" };
      case "PROJECT_IN_DELIVERY": return { label: "Continue project delivery", action: "deliver_project" };
      case "PROJECT_DELIVERED": return { label: "Offer retainer", action: "offer_retainer" };
      default: return { label: "Review opportunity", action: "review" };
    }
  };

  const nextAction = getNextAction();

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
        <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="text-2xl font-semibold text-foreground">
                {prospect.account?.name}
              </div>
              <StagePill stage={prospect.stage} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground/65">
              <span className="flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                {prospect.contact?.full_name || "No contact"}
              </span>
              <span className="flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" />
                {prospect.contact?.email || "No email"}
              </span>
              {prospect.account?.industry && (
                <span className="flex items-center gap-1">
                  <TrendingUp className="h-3.5 w-3.5" />
                  {prospect.account.industry}
                </span>
              )}
            </div>
          </div>

          {isConsultant && (
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={async () => {
                  await crmApi.mockBookInitialCall({ prospect_id: prospect.id }, session);
                  trackEvent("booked_call", { prospect_id: prospect.id });
                  analyticsApi.track("booked_call", { prospect_id: prospect.id }, session);
                  toast.success("Qualification call scheduled");
                  onRefresh();
                }}
                className="h-10 rounded-none border border-border bg-foreground/5 text-foreground hover:bg-foreground hover:text-background"
                variant="secondary"
                data-testid="book-call-btn"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Book Call
              </Button>
            </div>
          )}
        </div>

        {/* Stage Advancement Actions */}
        {isConsultant && (
          <StageActions
            session={session}
            prospect={prospect}
            stage={stage}
            onRefresh={onRefresh}
            assessmentStatus={assessmentStatus}
            pricing={pricing}
          />
        )}

        {/* Stage Progress Graphic */}
        <ProspectStageTracker stage={stage} />

        {/* Next Best Action */}
        {isConsultant && (
          <div className="mt-4 rounded-xl border border-[var(--brand)]/30 bg-[var(--brand)]/5 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--brand)]/10 text-[var(--brand)]">
                <ChevronRight className="h-4 w-4" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-foreground/55">Next Action</div>
                <div className="text-sm font-medium text-foreground">{nextAction.label}</div>
              </div>
            </div>
          </div>
        )}

        {/* Consultant Assignment */}
        {isConsultant && (
          <PhaseConsultantAssignment prospect={prospect} session={session} onRefresh={onRefresh} />
        )}

        {/* Proceed to Scope of Services & Project — visible after Scope Confirmed */}
        {isConsultant && stage === "SCOPE_CONFIRMED" && (
          <ProceedToSOWButton prospect={prospect} session={session} onRefresh={onRefresh} />
        )}
      </Card>

      {/* Details Card */}
      <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
        <Tabs defaultValue="overview">
          <TabsList className="rounded-none bg-muted">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="intake">Intake Data</TabsTrigger>
            <TabsTrigger value="assessment">Assessment</TabsTrigger>
            <TabsTrigger value="interactions">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-5">
            <div className="grid gap-4 md:grid-cols-3">
              {/* Stage Progress */}
              <Card className="rounded-2xl border-border bg-muted p-4 text-foreground/70">
                <div className="text-sm font-semibold text-foreground">Current Stage</div>
                <div className="mt-3">
                  <StagePill stage={prospect.stage} />
                </div>
                <div className="mt-4 text-xs text-foreground/45">
                  {PIPELINE_STAGES[stage]?.label || stage}
                </div>
              </Card>

              {/* Payments */}
              <Card className="rounded-2xl border-border bg-muted p-4 text-foreground/70">
                <div className="text-sm font-semibold text-foreground">Payments</div>
                <div className="mt-3 space-y-2 text-sm text-foreground/65">
                  <div className="flex items-center justify-between">
                    <span>Assessment ({pricing?.pricing?.assessment?.amount ? `$${Math.round(pricing.pricing.assessment.amount).toLocaleString()}` : "$4,999"})</span>
                    {prospect.payment?.detailed_assessment_paid ? (
                      <Check className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <span className="text-foreground/35">—</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Implementation ({pricing?.pricing?.implementation?.amount ? `$${Math.round(pricing.pricing.implementation.amount).toLocaleString()}` : "$9,999"})</span>
                    {prospect.payment?.roadmap_paid ? (
                      <Check className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <span className="text-foreground/35">—</span>
                    )}
                  </div>
                </div>
              </Card>

              {/* Quick Stats */}
              <Card className="rounded-2xl border-border bg-muted p-4 text-foreground/70">
                <div className="text-sm font-semibold text-foreground">Activity</div>
                <div className="mt-3 space-y-2 text-sm text-foreground/65">
                  <div className="flex items-center justify-between">
                    <span>Interactions</span>
                    <span className="text-foreground">{prospect.interactions?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Created</span>
                    <span className="text-foreground/55 text-xs">
                      {new Date(prospect.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="intake" className="mt-5">
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="rounded-2xl border-border bg-muted p-4">
                <div className="text-sm font-semibold text-foreground mb-3">Company Info</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-foreground/55">Company</span>
                    <span className="text-foreground">{prospect.account?.name || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground/55">Industry</span>
                    <span className="text-foreground">{prospect.account?.industry || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground/55">Size</span>
                    <span className="text-foreground">{prospect.intake_data?.company_size || prospect.account?.company_size || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground/55">Website</span>
                    <span className="text-foreground">
                      {prospect.intake_data?.website || prospect.account?.website ? (
                        <a href={prospect.intake_data?.website || prospect.account?.website} target="_blank" rel="noopener noreferrer" className="text-[var(--brand)] hover:underline truncate max-w-[200px] inline-block">
                          {(prospect.intake_data?.website || prospect.account?.website).replace(/^https?:\/\//, '')}
                        </a>
                      ) : "—"}
                    </span>
                  </div>
                </div>
              </Card>

              <Card className="rounded-2xl border-border bg-muted p-4">
                <div className="text-sm font-semibold text-foreground mb-3">Contact</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-foreground/55">Name</span>
                    <span className="text-foreground">{prospect.contact?.full_name || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground/55">Email</span>
                    <span className="text-foreground">{prospect.contact?.email || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground/55">Phone</span>
                    <span className="text-foreground">{prospect.contact?.phone || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground/55">Role / Title</span>
                    <span className="text-foreground">{prospect.intake_data?.role || prospect.contact?.title || "—"}</span>
                  </div>
                </div>
              </Card>

              {/* Pain Areas */}
              {prospect.intake_data?.pain_areas?.length > 0 && (
                <Card className="rounded-2xl border-border bg-muted p-4">
                  <div className="text-sm font-semibold text-foreground mb-3">Pain Areas</div>
                  <div className="flex flex-wrap gap-2">
                    {prospect.intake_data.pain_areas.map((p, i) => (
                      <span key={i} className="inline-flex items-center rounded-full border border-[#007a9e]/30 dark:border-amber-500/30 bg-[#007a9e]/10 dark:bg-amber-500/10 px-3 py-1 text-xs text-[#007a9e] dark:text-amber-400">
                        {p.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                </Card>
              )}

              {/* Urgency */}
              {prospect.intake_data?.urgency && (
                <Card className="rounded-2xl border-border bg-muted p-4">
                  <div className="text-sm font-semibold text-foreground mb-3">Urgency</div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[var(--brand)]" />
                    <span className="text-sm text-foreground">{prospect.intake_data.urgency} days</span>
                  </div>
                </Card>
              )}

              {/* Intake Notes */}
              {(prospect.intake_data?.notes || "") && (
                <Card className="rounded-2xl border-border bg-muted p-4 md:col-span-2">
                  <div className="text-sm font-semibold text-foreground mb-3">Intake Notes</div>
                  <p className="text-sm text-foreground/70">
                    {prospect.intake_data.notes}
                  </p>
                </Card>
              )}

              {/* AI Enrichment */}
              <Card className="rounded-2xl border-border bg-muted p-4 md:col-span-2" data-testid="ai-enrichment-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-base font-semibold text-foreground">AI-Enriched Insights</div>
                  <EnrichWithAIButton session={session} prospectId={prospect.id} existingEnrichment={prospect.ai_enrichment} onRefresh={onRefresh} />
                </div>
                {prospect.ai_enrichment ? (
                  <div className="space-y-5">
                    {prospect.ai_enrichment.company_summary && (
                      <div>
                        <div className="text-xs text-foreground/40 uppercase tracking-wider mb-1.5">Company Profile</div>
                        <p className="text-base text-foreground/70 leading-relaxed">{prospect.ai_enrichment.company_summary}</p>
                      </div>
                    )}
                    {prospect.ai_enrichment.industry_context && (
                      <div>
                        <div className="text-xs text-foreground/40 uppercase tracking-wider mb-1.5">Industry Context</div>
                        <p className="text-base text-foreground/70 leading-relaxed">{prospect.ai_enrichment.industry_context}</p>
                      </div>
                    )}
                    {prospect.ai_enrichment.pain_point_analysis && (
                      <div>
                        <div className="text-xs text-foreground/40 uppercase tracking-wider mb-1.5">Pain Point Analysis</div>
                        <p className="text-base text-foreground/70 leading-relaxed">{prospect.ai_enrichment.pain_point_analysis}</p>
                      </div>
                    )}
                    {prospect.ai_enrichment.recommended_focus && (
                      <div>
                        <div className="text-xs text-foreground/40 uppercase tracking-wider mb-1.5">Recommended Focus for Call</div>
                        <p className="text-base text-foreground/70 leading-relaxed">{prospect.ai_enrichment.recommended_focus}</p>
                      </div>
                    )}
                    {prospect.ai_enrichment.linkedin_insights && (
                      <div>
                        <div className="text-xs text-foreground/40 uppercase tracking-wider mb-1.5">Contact & LinkedIn Insights</div>
                        <p className="text-base text-foreground/70 leading-relaxed">{prospect.ai_enrichment.linkedin_insights}</p>
                      </div>
                    )}
                    {prospect.ai_enrichment.risk_factors && (
                      <div>
                        <div className="text-xs text-foreground/40 uppercase tracking-wider mb-1.5">Risk Factors</div>
                        <p className="text-base text-[#007a9e] dark:text-amber-400/80 leading-relaxed">{prospect.ai_enrichment.risk_factors}</p>
                      </div>
                    )}
                    {prospect.ai_enrichment.estimated_value && (
                      <div>
                        <div className="text-xs text-foreground/40 uppercase tracking-wider mb-1.5">Estimated Value</div>
                        <p className="text-base text-emerald-400 leading-relaxed">{prospect.ai_enrichment.estimated_value}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-foreground/40">
                    Click "Enrich with AI" to generate insights about this prospect based on their intake data.
                  </p>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="assessment" className="mt-5">
            <ProspectAssessment
              session={session}
              prospect={prospect}
              onAssessmentStatusChange={setAssessmentStatus}
            />
          </TabsContent>

          <TabsContent value="interactions" className="mt-5">
            <Card className="rounded-2xl border-border bg-muted p-4" data-testid="activity-log">
              <div className="text-sm font-semibold text-foreground mb-4">Activity Timeline</div>
              <div className="space-y-3">
                {/* Build timeline from prospect data */}
                {(() => {
                  const timeline = [];
                  // Created
                  if (prospect.created_at) {
                    timeline.push({ date: prospect.created_at, type: "milestone", summary: "Lead created — entered pipeline", icon: "intake" });
                  }
                  // Payment milestones
                  if (prospect.payment?.detailed_assessment_paid_at) {
                    timeline.push({ date: prospect.payment.detailed_assessment_paid_at, type: "payment", summary: "Paid Assessment purchased", icon: "payment" });
                  } else if (prospect.payment?.detailed_assessment_paid) {
                    timeline.push({ date: prospect.created_at, type: "payment", summary: "Paid Assessment purchased", icon: "payment" });
                  }
                  if (prospect.payment?.roadmap_paid_at) {
                    timeline.push({ date: prospect.payment.roadmap_paid_at, type: "payment", summary: "Implementation project payment recorded", icon: "payment" });
                  } else if (prospect.payment?.roadmap_paid) {
                    timeline.push({ date: prospect.created_at, type: "payment", summary: "Implementation project payment recorded", icon: "payment" });
                  }
                  // Provisioned tenant
                  if (prospect.provisioned_tenant_id) {
                    timeline.push({ date: prospect.updated_at || prospect.created_at, type: "milestone", summary: "Tenant portal provisioned", icon: "milestone" });
                  }
                  // Interactions (includes auto-logged stage changes, assignments, assessments)
                  (prospect.interactions || []).forEach(i => {
                    timeline.push({ date: i.created_at, type: i.type, summary: i.summary, icon: i.type, id: i.id });
                  });
                  // Sort by date descending
                  timeline.sort((a, b) => new Date(b.date) - new Date(a.date));
                  
                  if (!timeline.length) return null;

                  const dotColor = (icon) => {
                    if (icon === "payment") return "bg-emerald-500";
                    if (icon === "milestone" || icon === "intake") return "bg-[var(--brand)]";
                    if (icon === "stage_change") return "bg-blue-500";
                    if (icon === "assignment") return "bg-indigo-500";
                    if (icon === "assessment") return "bg-violet-500";
                    return "bg-foreground/30";
                  };

                  return timeline.map((item, idx) => (
                    <div key={item.id || idx} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`h-2.5 w-2.5 rounded-full shrink-0 mt-1.5 ${dotColor(item.icon)}`} />
                        {idx < timeline.length - 1 && <div className="w-px flex-1 bg-border min-h-[16px]" />}
                      </div>
                      <div className="pb-3 min-w-0">
                        <div className="text-sm text-foreground/80">{item.summary}</div>
                        <div className="text-xs text-foreground/40 mt-0.5">
                          {!["milestone", "payment", "stage_change", "assignment", "assessment"].includes(item.type) ? `${item.type} · ` : ""}
                          {new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at {new Date(item.date).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  ));
                })()}

                {!prospect.interactions?.length && !prospect.created_at && (
                  <div className="text-center py-6 text-sm text-foreground/55">
                    No activity recorded yet.
                  </div>
                )}

                {isConsultant && (
                  <div className="grid gap-3 md:grid-cols-[1fr_140px] mt-4 pt-4 border-t border-border">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="min-h-[44px] rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                      placeholder="Add a note…"
                    />
                    <Button
                      disabled={!newNote.trim()}
                      onClick={async () => {
                        await crmApi.addInteraction(
                          {
                            prospect_id: prospect.id,
                            type: "note",
                            summary: newNote.trim(),
                          },
                          session,
                        );
                        toast.message("Saved", { description: "Note added." });
                        setNewNote("");
                        onRefresh();
                      }}
                      className="h-11 rounded-none bg-[var(--brand)] text-white hover:bg-[var(--brand)]"
                    >
                      Add note
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* ─── Phase-Specific Consultant Assignment Panel ─── */
const PhaseConsultantAssignment = ({ prospect, session, onRefresh }) => {
  const [consultants, setConsultants] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/employees/lookup/consultants`)
      .then((r) => r.json())
      .then((d) => { setConsultants(d); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const handleAssign = async (field, consultantId) => {
    const emp = consultants.find((c) => c.id === consultantId);
    if (!emp) return;
    setAssigning(field);
    try {
      const payload = { prospect_id: prospect.id };
      payload[`${field}_id`] = consultantId;
      payload[`${field}_name`] = `${emp.first_name} ${emp.last_name}`;
      // Also update the legacy assigned_consultant for backward compat
      if (field === "free_assessment_consultant") {
        payload.assigned_consultant_id = consultantId;
        payload.assigned_consultant_name = `${emp.first_name} ${emp.last_name}`;
      }
      await crmApi.updateProspect(payload, session);
      toast.success(`${emp.first_name} ${emp.last_name} assigned`);
      onRefresh();
    } catch {
      toast.error("Failed to assign consultant");
    } finally {
      setAssigning(null);
    }
  };

  if (!loaded) return null;

  const phases = [
    { key: "free_assessment_consultant", label: "Free Assessment", icon: "FA" },
    { key: "detailed_assessment_consultant", label: "In-Depth Assessment", icon: "DA" },
    { key: "project_consultant", label: "Project", icon: "PR" },
  ];

  return (
    <div className="mt-3 rounded-xl border border-border bg-muted/60 p-3 space-y-2" data-testid="consultant-assignment">
      <div className="flex items-center gap-2 mb-1">
        <UserCheck className="h-4 w-4 text-indigo-400" />
        <span className="text-[10px] uppercase tracking-wider text-foreground/40 font-medium">Consultant Assignments</span>
      </div>
      {phases.map(({ key, label, icon }) => {
        const currentId = prospect[`${key}_id`] || (key === "free_assessment_consultant" ? prospect.assigned_consultant_id : "");
        const currentName = prospect[`${key}_name`] || (key === "free_assessment_consultant" ? prospect.assigned_consultant_name || (prospect.assigned_consultant ? `${prospect.assigned_consultant.first_name || ""} ${prospect.assigned_consultant.last_name || ""}`.trim() : "") : "");
        return (
          <div key={key} className="flex items-center gap-2">
            <span className="w-5 h-5 rounded bg-indigo-500/10 text-indigo-400 text-[9px] font-bold flex items-center justify-center shrink-0">{icon}</span>
            <span className="text-xs text-foreground/55 w-[120px] shrink-0 truncate">{label}</span>
            {currentId ? (
              <span className="text-xs font-medium text-foreground flex items-center gap-1 flex-1">
                {currentName || "Assigned"} <Check className="h-3 w-3 text-emerald-500" />
              </span>
            ) : (
              <span className="text-xs text-amber-400 flex items-center gap-1 flex-1">
                <AlertTriangle className="h-3 w-3" /> Unassigned
              </span>
            )}
            <select
              value={currentId || ""}
              onChange={(e) => e.target.value && handleAssign(key, e.target.value)}
              disabled={assigning === key}
              className="h-7 rounded-md border border-border bg-background text-[11px] text-foreground px-1.5 w-[145px]"
              data-testid={`assign-${key}-select`}
            >
              <option value="">{currentId ? "Change…" : "Select…"}</option>
              {consultants.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name}
                </option>
              ))}
            </select>
            {assigning === key && <Loader2 className="h-3 w-3 animate-spin text-foreground/40" />}
          </div>
        );
      })}
    </div>
  );
};

/* ─── Proceed to Scope of Services & Project Button ─── */
const ProceedToSOWButton = ({ prospect, session, onRefresh }) => {
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleProceed = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${BACKEND}/api/prospects/${prospect.id}/proceed-to-sow`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": session.tenant?.id || "tenant_1",
          "x-user-id": session.user?.id || "admin",
          "x-user-role": session.user?.role || "admin",
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setResult(data);
      toast.success("Tenant provisioned & Scope of Services generated!", { description: data.message?.substring(0, 80) });
      onRefresh();
    } catch (e) {
      toast.error("Failed to proceed", { description: e.message });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="proceed-to-sow-section">
      {result ? (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-emerald-400 flex items-center gap-2">
            <Check className="h-4 w-4" /> Tenant Portal Ready
          </div>
          <div className="text-xs text-foreground/70 space-y-1">
            <div>Portal Link: <a href={result.invite_link} target="_blank" rel="noopener noreferrer" className="text-[var(--brand)] hover:underline">{result.invite_link}</a></div>
            <div>Credentials: <span className="font-mono text-foreground/50">{result.credentials?.username} / {result.credentials?.password}</span></div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-foreground">Proceed to Scope of Services & Project</div>
            <div className="text-xs text-foreground/50 mt-0.5">Provision tenant, generate Scope of Services, and send portal invite</div>
          </div>
          <Button
            onClick={handleProceed}
            disabled={processing}
            className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4"
            data-testid="proceed-to-sow-btn"
          >
            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {processing ? "Processing…" : "Proceed"}
          </Button>
        </div>
      )}
    </div>
  );
};

const CUSTOMER_STAGE_SET = new Set([
  "ASSESSMENT_PURCHASED", "ASSESSMENT_IN_PROGRESS", "ASSESSMENT_DELIVERED",
  "SCOPE_CONFIRMED", "SOW_SENT", "SOW_APPROVED",
  "PROJECT_KICKOFF", "PROJECT_ACTIVE", "PROJECT_IN_DELIVERY", "PROJECT_COMPLETED", "PROJECT_DELIVERED",
  "RETAINER_OFFERED", "RETAINER_ACTIVE", "AT_RISK", "CHURNED",
]);

export const PipelineScreen = ({ session, mode = "leads" }) => {
  const [selectedId, setSelectedId] = useState("");
  const [tick, setTick] = useState(0);
  const [prospectsRaw, setProspectsRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState(null);

  const refresh = () => setTick((x) => x + 1);

  // Filter by mode — Leads (pre-paid) or Customers (post-paid in-depth assessment)
  const prospects = React.useMemo(() => {
    return prospectsRaw.filter((p) => {
      const stage = normalizeStage(p.stage);
      const isCustomer = CUSTOMER_STAGE_SET.has(stage);
      return mode === "customers" ? isCustomer : !isCustomer;
    });
  }, [prospectsRaw, mode]);

  // Fetch dynamic pricing from backend
  useEffect(() => {
    fetch(`${BACKEND}/api/pricing/public`)
      .then(r => r.json())
      .then(d => setPricing(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const list = await crmApi.listProspects(
          {
            assigned_to_user_id:
              session.user.role === "consultant" ? session.user.id : undefined,
          },
          session,
        );
        const expanded = await Promise.all(
          list.map((p) => crmApi.getProspect({ prospect_id: p.id }, session)),
        );
        if (mounted) setProspectsRaw(expanded);
      } catch (e) {
        toast.error("Could not load pipeline", {
          description: e?.response?.data?.detail || e?.message || "Try again.",
        });
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [session, tick]);

  useEffect(() => {
    if (prospects.length && !prospects.some((p) => p.id === selectedId)) {
      setSelectedId(prospects[0].id);
    } else if (!prospects.length && selectedId) {
      setSelectedId("");
    }
  }, [prospects, selectedId]);

  // Reset selection when switching Leads ↔ Customers
  useEffect(() => { setSelectedId(""); }, [mode]);

  const pageTitle = session.user.role === "admin"
    ? (mode === "customers" ? "Customers" : "Leads")
    : session.user.role === "consultant"
      ? "My Workbench"
      : "Dashboard";
  const pageSubtitle = session.user.role === "admin"
    ? (mode === "customers"
        ? "Active customers — from In-Depth Assessment through Retainer"
        : "Prospects in the sales funnel through the Free Assessment")
    : session.user.role === "consultant"
      ? "Your active opportunities and tasks"
      : "Welcome to your 589 Labs portal";

  return (
    <div className="px-4 py-8 sm:px-6 bg-[#f9fafb] min-h-screen">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div>
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="text-[24px] font-bold text-[#111827] tracking-[-0.01em]" data-testid="pipeline-page-title">
                {pageTitle}
              </div>
              <div className="mt-1 text-[13px] text-[#9ca3af]">
                {pageSubtitle}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={refresh}
              className="h-9 rounded-[8px] border-[#e5e7eb] bg-white text-[#374151] text-[12px] font-semibold hover:bg-[#f9fafb]"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-4">
            {session.user.role === "admin" ? (
              <AdminSalesDashboard prospects={prospectsRaw} pricing={pricing} />
            ) : session.user.role === "consultant" ? (
              <ConsultantWorkbench prospects={prospects} session={session} />
            ) : (
              <TenantDashboard tenantId={session.tenant?.id} />
            )}
          </div>
        </div>

        {/* Pipeline list */}
        <div>
          <PipelineTable
            prospects={prospects}
            selectedId={selectedId}
            onSelect={setSelectedId}
            loading={loading}
            isAdmin={session.user.role === "admin"}
            pricing={pricing}
            mode={mode}
          />
        </div>

        {/* Prospect details */}
        {selectedId ? (
          <ProspectPanel
            session={session}
            prospectId={selectedId}
            onRefresh={refresh}
            pricing={pricing}
          />
        ) : (
          <div className="rounded-[14px] border border-[#e5e7eb] bg-white p-10 text-center text-[13px] text-[#9ca3af]">
            {loading ? "Loading…" : (mode === "customers" ? "Select a customer to view details." : "Select a lead to view details.")}
          </div>
        )}
      </div>
    </div>
  );
};
